import * as History from 'history'
import * as ReduxObservable from 'redux-observable'
import {Observable} from 'rxjs'
import {map, mapTo, filter, tap, withLatestFrom} from 'rxjs/operators'
import * as Actions from './actions'
import {GO_BACK, PUSH, REDIRECT, REPLACE} from './constants'

interface Dependencies {
  history: History.History,
  history$: Observable<[History.LocationDescriptorObject]>,
  window: any,
}

const canLeavePage = (action: any, store: any): boolean => {
  // We can leave the page if:
  // a) We don't have any state yet, or
  // b) The state doesn't require confirmation, or
  // c) The current page is flagged to skip confirmation, or
  // d) The user confirms a message
  return !store.value.router ||
    !store.value.router.showLeaveWarning ||
    (store.value.router.skipUrls || []).includes(action.payload.href.toLocaleLowerCase()) ||
    window.confirm(store.value.router.leaveWarningMessage)
}

type PushEpic = ReduxObservable.Epic<Actions.PushAction, any, any, Dependencies>
export const pushEpic: PushEpic = (action$, store, {history, history$}) =>
action$.ofType(PUSH).pipe(
  filter((action) => canLeavePage(action, store)),
  tap((action) => history.push(action.payload.href)),
  withLatestFrom(history$),
  map(([action, [location]]) =>
    Actions.locationChange({
      hash: location.hash,
      pathname: location.pathname,
      search: location.search,
    })
  )
)

type ReplaceEpic = ReduxObservable.Epic<Actions.ReplaceAction, any, any, Dependencies>
export const replaceEpic: ReplaceEpic = (action$, store, {history, history$}) =>
action$.ofType(REPLACE).pipe(
  filter((action) => canLeavePage(action, store)),
  tap((action) => history.replace(action.payload.href)),
  withLatestFrom(history$),
  map(([action, [location]]) =>
    Actions.locationChange({
      hash: location.hash,
      pathname: location.pathname,
      search: location.search,
    })
  )
)

type RedirectEpic = ReduxObservable.Epic<Actions.RedirectAction, any, any, Dependencies>
export const redirectEpic: RedirectEpic = (action$, store, {window}) =>
action$.ofType(REDIRECT).pipe(
  filter((action) => canLeavePage(action, store)),
  tap((action) => window.location.href = action.payload.href),
  mapTo({type: 'REDIRECTED'}),
)

type GoBackEpic = ReduxObservable.Epic<Actions.GoAction, any, any, Dependencies>
export const goBackEpic: GoBackEpic = (action$, store, {history, history$}) =>
action$.ofType(GO_BACK).pipe(
  filter((action) => canLeavePage(action, store)),
  tap(() => history.goBack()),
  withLatestFrom(history$),
  map(([action, [location]]) =>
    Actions.locationChange({
      hash: location.hash,
      pathname: location.pathname,
      search: location.search,
    })
  )
)

export const epic = ReduxObservable.combineEpics(
  goBackEpic,
  pushEpic,
  redirectEpic,
  replaceEpic,
)
